import React from 'react';

import { formatDate, mString } from 'lib/helpers/utility';

import ProceduresCell from 'components/practice/BeyondBilling/PatientLedger/components/ProceduresCell';
import {
  APPOINTMENT_RESOURCE_OPTIONS,
  APPOINTMENT_TAG_OPTIONS,
  EXTERNAL_INVOICE_OPTIONS,
  INVOICE_OPTIONS,
  PATIENT_OPTIONS,
  PROVIDER_OPTIONS,
  TAG_INVOICE_OPTIONS
} from 'components/shared/Filters/lib/filterOptions';
import TagsCell from 'components/shared/Tags/components/Cell/TagsCell';

import SendInvoiceMessagePopUp from '../../components/SendInvoiceMessagePopUp';

export const MemoComponent = ({ value }) => {
  return (
    <div
      className="text-sm font-medium leading-5 text-neutral-700"
      dangerouslySetInnerHTML={{
        __html: value
      }}
    />
  );
};

export const getColDefs = (timezone) => [
  {
    field: 'id',
    headerName: 'Invoice ID',
    cellClass: 'no-border',
    maxWidth: 150
  },
  {
    field: 'patient_name',
    headerName: 'Patient',
    cellClass: 'no-border'
  },
  {
    field: 'created_at',
    headerName: 'Invoice Date',
    valueFormatter: ({ value }) => formatDate(value, timezone, true),
    cellClass: 'no-border'
  },
  {
    field: 'delivery_method',
    headerName: 'Delivery Method',
    valueGetter: (params) => {
      const deliveryMethod = params?.data?.delivery_method;
      if (!deliveryMethod) return '-';
      const methodUsed = Object.keys(deliveryMethod).filter(
        (key) => deliveryMethod[key]?.total > 0
      );

      return methodUsed.join(', ').toUpperCase();
    },
    cellClass: 'no-border'
  },
  {
    field: 'total_amount_cents',
    headerName: 'Amount',
    aggFunc: (params) => {
      let sum = 0;
      params.values.forEach((value) => (sum += value));
      return sum;
    },
    valueFormatter: ({ value }) => mString(value || 0),
    cellClass: 'no-border'
  },
  {
    field: 'paidStatus',
    headerName: 'Status',
    cellClass: 'no-border'
  },
  { field: 'memo', cellRenderer: MemoComponent, cellClass: 'no-border', headerName: 'Memo' },
  { field: 'internal_invoice_ids', headerName: 'Internal Invoices', cellClass: 'no-border' },
  {
    headerName: 'CPT® Codes',
    field: 'procedures',
    sortable: false,
    cellRenderer: ({ data, node }) => (
      <ProceduresCell node={node} procedureCodes={data?.procedures} />
    )
  },
  {
    field: 'tagLinks',
    headerName: 'Tags',
    cellClass: 'no-border',
    minWidth: 300,
    cellRenderer: ({ value, data }) => (
      <TagsCell
        value={value}
        kind="invoice"
        data={data}
        query={{ keys: ['external_invoices', 'invoice-tags'], object: 'invoices', multiple: true }}
        refTable="invoice"
      />
    ),
    pinned: 'right',
    sortable: false
  },
  {
    field: 'actions',
    headerName: 'Actions',
    cellRenderer: SendInvoiceMessagePopUp,
    cellClass: 'no-border',
    maxWidth: 88,
    minWidth: 36,
    resizable: false,
    sortable: false,
    pinned: 'right'
  }
];

export const DEFAULT_COLUMN_DEFS = { resizable: true, enableRowGroup: true };

export const GRID_OPTIONS = {
  rowGroupPanelShow: 'always',
  animateRows: false,
  groupSelectsChildren: true,
  enableRangeSelection: true,
  suppressAggFuncInHeader: true
};

const STATUS_OPTIONS = [
  { label: 'Paid', value: 'Paid' },
  { label: 'Partially Paid', value: 'Partially Paid' },
  { label: 'Not Paid', value: 'Not Paid' }
];

export const DEFAULT_FILTERS = {
  date: {
    type: 'date-range',
    title: 'Date of Invoice',
    icon: 'calendar-mono'
  },
  external_invoice_id: EXTERNAL_INVOICE_OPTIONS,
  patient: PATIENT_OPTIONS,
  provider: { ...PROVIDER_OPTIONS, allowed_operators: ['is any of', 'is not', 'is'] },
  amount: {
    type: 'range',
    title: 'Amount',
    icon: 'dollar-circle-mono'
  },
  appointment_resource: APPOINTMENT_RESOURCE_OPTIONS,
  appointmentTag: APPOINTMENT_TAG_OPTIONS,
  internal_invoice_id: {
    ...INVOICE_OPTIONS,
    title: 'Internal Invoice',
    allowed_operators: ['is any of', 'is not', 'is']
  },
  invoiceTag: TAG_INVOICE_OPTIONS,
  status: {
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: STATUS_OPTIONS.filter((status) =>
          status.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'external_invoice_statuses.options',
    title: 'Status',
    icon: 'status-mono',
    allowed_operators: ['is any of', 'is not', 'is']
  }
};
